'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MainNavbar } from '@/components/layout/MainNavbar';
import { Footer } from '@/components/Footer';
import { useSettings } from '@/contexts/SettingsContext';
import { Home, Calculator, RefreshCw, AlertTriangle, Bug } from 'lucide-react';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

function ErrorContent({ error, reset }: ErrorProps) {
  const settings = useSettings();

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50 flex flex-col">
      <MainNavbar 
        currency={settings.currency}
        onCurrencyChange={settings.setCurrency}
      />
      
      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl flex items-center justify-center">
        <Card className="w-full max-w-2xl shadow-xl border-red-200 dark:border-red-800">
          <CardContent className="p-12 text-center">
            {/* Error Icon */}
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-10 w-10 text-red-600 dark:text-red-400" />
              </div>
              <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full"></div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-bold mb-4 text-foreground">
              Something Went Wrong
            </h1>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              We encountered an unexpected error while loading this page. Don&apos;t worry, 
              this has been logged and we&apos;re working to fix it.
            </p>

            {/* Error Details (for development) */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-8 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-left">
                <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center gap-2">
                  <Bug className="h-4 w-4" />
                  Error Details (Development Only)
                </h3>
                <code className="text-sm text-red-700 dark:text-red-300 break-all">
                  {error.message}
                </code>
                {error.digest && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    Error ID: {error.digest}
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button 
                size="lg" 
                onClick={reset}
                className="bg-accessnow-orange hover:bg-accessnow-orange/90 text-white shadow-lg"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Try Again
              </Button>
              
              <Link href="/">
                <Button size="lg" variant="outline" className="border-accessnow-orange text-accessnow-orange hover:bg-accessnow-orange hover:text-white">
                  <Home className="h-5 w-5 mr-2" />
                  Go Home
                </Button>
              </Link>
            </div>

            {/* Additional Actions */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/calculator">
                  <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                    <Calculator className="h-4 w-4 mr-2" />
                    Back to Calculator
                  </Button>
                </Link>
              </div>

              {/* Help Text */}
              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  If this problem persists, please refresh the page or contact support. 
                  We apologize for the inconvenience.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer 
        showDebugModal={false}
        showChangelog={true}
        showHomeLink={true}
      />
    </div>
  );
}

export default function Error({ error, reset }: ErrorProps) {
  return (
    <ErrorContent error={error} reset={reset} />
  );
}